import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { DeploymentConfigurationService } from "@application/useCases/workspace/DeploymentConfigurationService";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { rowStyle } from "@presentation/common/pageLayout";
import RegistryVersionsSelector from "@presentation/components/softwareVersions/RegistryVersionsSelector";
import type { EffectiveSoftwareVersionContextConfiguration, SoftwareEnumSchema, SoftwareVersionContextConfiguration } from "@domain/schemas/BI";
import type { SupportedSoftware } from "@domain/repositories/ISoftwareVersionsRepository";
import { isSoftwareLicensed, useLicensedSoftware, type LicensedSoftwareState } from "@presentation/components/job/components/useLicensedSoftware";
import { LicenseRequiredMessage } from "@presentation/components/permission/LicenseRequiredMessage";

// `BI.schema.d.ts` enums have no runtime object under Vitest (ambient/type-only in a .d.ts file;
// Vite's esbuild transpiles it anyway, so production is unaffected) - string literals cast to the
// enum type sidestep that, matching this codebase's established workaround.
// SpectroDive/Quic are not supported yet (no registry, no allow-list UI) - omitted until they are.
const SOFTWARE_LIST = ["Spectronaut", "SpectroMine"] as SoftwareEnumSchema[];
// Engines whose candidate versions come from a live registry/license feed get the grouped,
// searchable picker; every other engine falls back to a plain manual add/remove list, mirroring
// `ISoftwareVersionsRepository`'s `SupportedSoftware` union.
const REGISTRY_BACKED_SOFTWARE = new Set<SoftwareEnumSchema>(["Spectronaut", "SpectroMine"] as SoftwareEnumSchema[]);
const EMPTY_EFFECTIVE_SOFTWARE_VERSION_CONFIGURATION: EffectiveSoftwareVersionContextConfiguration = { allowLists: {}, defaults: {} };

const licenseStatusMessage = (state: LicensedSoftwareState): string => {
  if (state.status === "error") return "Could not load license status. Retrying...";
  return state.status === "unknown" ? "Waiting for the cluster to report license status..." : "Loading license status...";
};

interface SoftwareVersionConfigurationSectionProps {
  level: "deployment" | "team" | "project";
  /**
  Required for "team"/"project"; ignored for "deployment" (singleton, no owning id).
  */
  contextId?: string;
  /**
  Own-rows CRUD controls are hidden (never disabled) when false, per WP4.0.3's rendering convention.
  */
  canManage: boolean;
}

/**
One component for every level (deployment/team/project). Registry-backed engines (Spectronaut,
SpectroMine) render as a grouped/searchable selector against the resolved effective allow-list;
every other engine (SpectroDive, Quic) renders as a plain manual add/remove list against this
level's own override rows, with "Set default"/"Clear default" available against the resolved
effective version regardless of which level owns the underlying allow-list row - the backend
resolves a level's default independently of its allow-list (a level may pin a default while still
inheriting the allow-list itself). Deployment has no parent to resolve against, so its own rows
already are its effective configuration.
*/
export const SoftwareVersionConfigurationSection: React.FC<SoftwareVersionConfigurationSectionProps> = ({ level, contextId, canManage }) => {
  const deploymentConfigurationService = useInjection(DeploymentConfigurationService);
  const teamManagementService = useInjection(TeamManagementService);
  const listTeamsService = useInjection(ListTeamsService);
  const projectManagementService = useInjection(ProjectManagementService);
  const versionsService = useInjection(ListSoftwareVersionsService);
  const licenseState = useLicensedSoftware();
  const licensedSoftware = licenseState.status === "ready" ? licenseState.licensed : null;

  const [ownConfiguration, setOwnConfiguration] = useState<SoftwareVersionContextConfiguration | null>(null);
  const [effectiveConfiguration, setEffectiveConfiguration] = useState<EffectiveSoftwareVersionContextConfiguration>(EMPTY_EFFECTIVE_SOFTWARE_VERSION_CONFIGURATION);
  const [candidateVersionsBySoftware, setCandidateVersionsBySoftware] = useState<Partial<Record<SoftwareEnumSchema, string[]>>>({});
  const [registryLoadingBySoftware, setRegistryLoadingBySoftware] = useState<Partial<Record<SoftwareEnumSchema, boolean>>>({});
  const [registryErrorBySoftware, setRegistryErrorBySoftware] = useState<Partial<Record<SoftwareEnumSchema, string>>>({});
  const [newVersionBySoftware, setNewVersionBySoftware] = useState<Partial<Record<SoftwareEnumSchema, string>>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /* eslint-disable unicorn/no-declarations-before-early-exit -- every handler below is used by the JSX returned after the component's own early return */
  const reload = useCallback(async () => {
    if (level === "deployment") {
      setOwnConfiguration(await deploymentConfigurationService.getSoftwareVersionConfigurationAsync());
      return;
    }

    if (!contextId) return;

    try {
      setEffectiveConfiguration(
        level === "team" ? await listTeamsService.getEffectiveSoftwareVersionConfigurationAsync(contextId) : await projectManagementService.getEffectiveSoftwareVersionConfigurationAsync(contextId),
      );
    } catch {
      setEffectiveConfiguration(EMPTY_EFFECTIVE_SOFTWARE_VERSION_CONFIGURATION);
    }

    if (!canManage) {
      setOwnConfiguration(null);
      return;
    }
    try {
      setOwnConfiguration(
        level === "team" ? await teamManagementService.getSoftwareVersionConfigurationAsync(contextId) : await projectManagementService.getSoftwareVersionConfigurationAsync(contextId),
      );
    } catch {
      setOwnConfiguration(null);
    }
  }, [level, contextId, canManage, deploymentConfigurationService, listTeamsService, teamManagementService, projectManagementService]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let isCancelled = false;
    const licensedEngines: readonly string[] = licensedSoftware ?? [];
    const registryBackedSoftware = [...REGISTRY_BACKED_SOFTWARE].filter((software) => licensedEngines.includes(software));
    setCandidateVersionsBySoftware({});
    setRegistryLoadingBySoftware(Object.fromEntries(registryBackedSoftware.map((software) => [software, true])));
    setRegistryErrorBySoftware({});
    // Each engine is fetched and reported independently - one engine's registry being
    // unreachable (e.g. a repository that hasn't been created yet) must not hide another
    // engine's candidates that loaded successfully.
    for (const software of registryBackedSoftware) {
      // `SoftwareEnumSchema` is a superset enum; every member of `REGISTRY_BACKED_SOFTWARE` is
      // known to also be a `SupportedSoftware`, which is what the registry service accepts.
      const supportedSoftware = software as unknown as SupportedSoftware;
      void versionsService
        .getCandidateVersionsListAsync(supportedSoftware)
        .then((versions) => {
          if (isCancelled) return;
          setCandidateVersionsBySoftware((state) => ({ ...state, [software]: versions }));
        })
        .catch(() => {
          if (isCancelled) return;
          setRegistryErrorBySoftware((state) => ({ ...state, [software]: `Failed to load candidate ${software} versions.` }));
        })
        .finally(() => {
          if (isCancelled) return;
          setRegistryLoadingBySoftware((state) => ({ ...state, [software]: false }));
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [versionsService, licensedSoftware]);

  const addVersion = useCallback(
    (software: SoftwareEnumSchema, version: string) => {
      if (level === "deployment") return deploymentConfigurationService.addSoftwareVersionAsync(software, version);
      return level === "team" ? teamManagementService.addSoftwareVersionAsync(contextId!, software, version) : projectManagementService.addSoftwareVersionAsync(contextId!, software, version);
    },
    [level, contextId, deploymentConfigurationService, teamManagementService, projectManagementService],
  );

  const removeVersion = useCallback(
    (versionId: string) => {
      if (level === "deployment") return deploymentConfigurationService.removeSoftwareVersionAsync(versionId);
      return level === "team" ? teamManagementService.removeSoftwareVersionAsync(contextId!, versionId) : projectManagementService.removeSoftwareVersionAsync(contextId!, versionId);
    },
    [level, contextId, deploymentConfigurationService, teamManagementService, projectManagementService],
  );

  const setDefaultVersion = useCallback(
    (software: SoftwareEnumSchema, version: string) => {
      if (level === "deployment") return deploymentConfigurationService.setSoftwareVersionDefaultAsync(software, version);
      return level === "team"
        ? teamManagementService.setSoftwareVersionDefaultAsync(contextId!, software, version)
        : projectManagementService.setSoftwareVersionDefaultAsync(contextId!, software, version);
    },
    [level, contextId, deploymentConfigurationService, teamManagementService, projectManagementService],
  );

  const clearDefaultVersion = useCallback(
    (software: SoftwareEnumSchema) => {
      if (level === "deployment") return deploymentConfigurationService.clearSoftwareVersionDefaultAsync(software);
      return level === "team" ? teamManagementService.clearSoftwareVersionDefaultAsync(contextId!, software) : projectManagementService.clearSoftwareVersionDefaultAsync(contextId!, software);
    },
    [level, contextId, deploymentConfigurationService, teamManagementService, projectManagementService],
  );

  const ownVersionRowsFor = (software: SoftwareEnumSchema) => (ownConfiguration?.versions ?? []).filter((entry) => entry.software === software);

  const effectiveVersionsFor = (software: SoftwareEnumSchema): string[] =>
    level === "deployment"
      ? ownVersionRowsFor(software)
          .map((entry) => entry.version)
          .filter((version): version is string => Boolean(version))
      : (effectiveConfiguration.allowLists?.[software]?.versions ?? []);

  const effectiveDefaultFor = (software: SoftwareEnumSchema): string | null =>
    level === "deployment" ? ((ownConfiguration?.defaults ?? []).find((entry) => entry.software === software)?.version ?? null) : (effectiveConfiguration.defaults?.[software]?.version ?? null);

  const handleSelectedVersionsChange = async (software: SoftwareEnumSchema, nextVersions: string[]) => {
    if (!canManage) return;
    setErrorMessage(null);
    const ownRows = ownVersionRowsFor(software);
    // Diff against this level's own override rows when it has any, else against the inherited
    // (effective) set - mirrors the backend's "full replacement, never merge" semantics without
    // requiring the caller to resend every unchanged version on every edit.
    const baseline = new Set(ownRows.length > 0 ? ownRows.map((entry) => entry.version).filter((version): version is string => Boolean(version)) : effectiveVersionsFor(software));
    const nextSet = new Set(nextVersions);
    const toAdd = nextVersions.filter((version) => !baseline.has(version));
    // eslint-disable-next-line unicorn/prefer-set-methods -- `Set#difference` needs ES2024 lib; tsconfig targets ES2023
    const toRemove = [...baseline].filter((version) => !nextSet.has(version));
    try {
      for (const version of toAdd) await addVersion(software, version);
      for (const version of toRemove) {
        const row = ownRows.find((entry) => entry.version === version);
        if (row?.id) await removeVersion(row.id);
      }
      await reload();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? `Could not update the allowed ${software} versions.`);
    }
  };

  const handleDefaultVersionChange = async (software: SoftwareEnumSchema, version: string) => {
    if (!canManage || !version) return;
    setErrorMessage(null);
    try {
      // The backend validates the new default against the effective allow-list, so a version
      // that isn't allowed yet (e.g. picked via the "set latest as default" shortcut) must be
      // added first.
      const currentlyAllowed = new Set([...ownVersionRowsFor(software).map((entry) => entry.version), ...effectiveVersionsFor(software)]);
      if (!currentlyAllowed.has(version)) await addVersion(software, version);
      await setDefaultVersion(software, version);
      await reload();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? `Could not update the default ${software} version.`);
    }
  };

  const handleClearDefault = async (software: SoftwareEnumSchema) => {
    setErrorMessage(null);
    try {
      await clearDefaultVersion(software);
      await reload();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not clear the default version.");
    }
  };

  const handleAddManualVersion = async (software: SoftwareEnumSchema) => {
    const version = (newVersionBySoftware[software] ?? "").trim();
    if (!version) return;
    setErrorMessage(null);
    try {
      await addVersion(software, version);
      setNewVersionBySoftware((state) => ({ ...state, [software]: "" }));
      await reload();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not add this version.");
    }
  };

  const handleRemoveManualVersion = async (versionId: string) => {
    setErrorMessage(null);
    try {
      await removeVersion(versionId);
      await reload();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not remove this version.");
    }
  };
  /* eslint-enable unicorn/no-declarations-before-early-exit */

  if (level === "deployment" && !ownConfiguration) return null;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {errorMessage && <p style={{ color: "#7f1d1d" }}>{errorMessage}</p>}

      {SOFTWARE_LIST.map((software) => {
        if (REGISTRY_BACKED_SOFTWARE.has(software)) {
          return (
            <div key={software} style={{ display: "grid", gap: 8 }}>
              <strong>{software}</strong>
              {isSoftwareLicensed(licenseState, software) ? (
                <RegistryVersionsSelector
                  softwareLabel={software}
                  candidateVersions={candidateVersionsBySoftware[software] ?? []}
                  selectedVersions={effectiveVersionsFor(software)}
                  defaultVersion={effectiveDefaultFor(software)}
                  ownVersions={
                    level === "deployment"
                      ? undefined
                      : ownVersionRowsFor(software)
                          .map((entry) => entry.version)
                          .filter((version): version is string => Boolean(version))
                  }
                  loading={registryLoadingBySoftware[software] ?? false}
                  error={registryErrorBySoftware[software] ?? ""}
                  disabled={!canManage}
                  emptyMessage={`No candidate ${software} versions are available.`}
                  onSelectedVersionsChange={(versions) => void handleSelectedVersionsChange(software, versions)}
                  onDefaultVersionChange={(version) => void handleDefaultVersionChange(software, version)}
                />
              ) : (
                <p role="status">{licenseState.status === "ready" ? <LicenseRequiredMessage software={software} /> : licenseStatusMessage(licenseState)}</p>
              )}
            </div>
          );
        }

        const effectiveVersions = effectiveVersionsFor(software);
        const effectiveDefault = effectiveDefaultFor(software);
        const ownRowIdByVersion = new Map(
          ownVersionRowsFor(software)
            .filter((entry) => entry.version)
            .map((entry) => [entry.version as string, entry.id as string]),
        );
        const hasOwnDefault = (ownConfiguration?.defaults ?? []).some((entry) => entry.software === software);

        return (
          <div key={software} style={{ display: "grid", gap: 8 }}>
            <strong>{software}</strong>
            <div style={{ display: "grid", gap: 4 }}>
              {effectiveVersions.map((version) => {
                const isDefault = version === effectiveDefault;
                const ownRowId = ownRowIdByVersion.get(version);
                // Deployment has no parent to inherit from, so every effective row is already its own.
                const origin = level === "deployment" ? null : ownRowId ? "Own" : "Inherited"; // eslint-disable-line unicorn/no-nested-ternary -- two mutually exclusive origins mapped to labels
                return (
                  <div key={version} style={rowStyle}>
                    <span>
                      {version} {isDefault && <span style={{ color: "#64748b" }}>(default)</span>} {origin && <span style={{ color: "#64748b" }}>({origin})</span>}
                    </span>
                    {canManage && (
                      <div style={{ display: "flex", gap: 8 }}>
                        {isDefault && hasOwnDefault ? (
                          <Button type="button" fillMode="flat" onClick={() => void handleClearDefault(software)}>
                            Clear default
                          </Button>
                        ) : (
                          <Button type="button" fillMode="flat" onClick={() => void handleDefaultVersionChange(software, version)}>
                            Set default
                          </Button>
                        )}
                        {ownRowId && (
                          <Button type="button" fillMode="flat" themeColor="error" onClick={() => void handleRemoveManualVersion(ownRowId)}>
                            Remove
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {canManage && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Version"
                    style={{ flex: 1, minWidth: 0 }}
                    value={newVersionBySoftware[software] ?? ""}
                    onChange={(event) => setNewVersionBySoftware((state) => ({ ...state, [software]: String(event.value ?? "") }))}
                  />
                  <Button type="button" onClick={() => void handleAddManualVersion(software)}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SoftwareVersionConfigurationSection;
